import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BroadcastChannel } from 'broadcast-channel';

type SessionMessage =
  | { type: 'SESSION_PING'; fromSessionId: string }
  | { type: 'SESSION_PONG'; fromSessionId: string; toSessionId: string }
  | { type: 'NEW_SESSION'; sessionId: string };

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly channel = new BroadcastChannel<SessionMessage>('lbri-session');
  private readonly tabSessionKey = 'lbri-tab-session-id';
  private readonly activeSessionKey = 'lbri-active-session-id';

  private invalidatedSubject = new BehaviorSubject<boolean>(false);
  invalidated$ = this.invalidatedSubject.asObservable();

  private newSessionNoticeSubject = new BehaviorSubject<boolean>(false);
  newSessionNotice$ = this.newSessionNoticeSubject.asObservable();

  private waitingForPong = false;
  private gotPong = false;

  constructor() {
    this.channel.onmessage = (msg) => {
      const mySessionId = this.getCurrentSessionId();

      if (!mySessionId) {
        return;
      }

      if (msg.type === 'SESSION_PING' && msg.fromSessionId !== mySessionId) {
        this.channel.postMessage({
          type: 'SESSION_PONG',
          fromSessionId: mySessionId,
          toSessionId: msg.fromSessionId
        });
        return;
      }

      if (msg.type === 'SESSION_PONG' && msg.toSessionId === mySessionId) {
        this.gotPong = true;
        return;
      }

      if (msg.type === 'NEW_SESSION' && msg.sessionId !== mySessionId) {
        this.invalidatedSubject.next(true);
        this.newSessionNoticeSubject.next(false);
      }
    };

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.activeSessionKey) {
        const mySessionId = this.getCurrentSessionId();
        const activeSessionId = event.newValue;

        if (mySessionId && activeSessionId && mySessionId !== activeSessionId) {
          this.invalidatedSubject.next(true);
          this.newSessionNoticeSubject.next(false);
        }
      }
    });

    window.addEventListener('focus', () => {
      const mySessionId = this.getCurrentSessionId();
      const activeSessionId = localStorage.getItem(this.activeSessionKey);

      if (mySessionId && activeSessionId && mySessionId !== activeSessionId) {
        this.invalidatedSubject.next(true);
        this.newSessionNoticeSubject.next(false);
      }
    });
  }

  async startSession(sessionId?: string): Promise<void> {
    const newSessionId = sessionId ?? crypto.randomUUID();

    sessionStorage.setItem(this.tabSessionKey, newSessionId);
    this.invalidatedSubject.next(false);
    this.newSessionNoticeSubject.next(false);

    // Ask if any live tab is actually out there
    this.waitingForPong = true;
    this.gotPong = false;

    this.channel.postMessage({
      type: 'SESSION_PING',
      fromSessionId: newSessionId
    });

    await new Promise((resolve) => setTimeout(resolve, 350));

    this.waitingForPong = false;

    localStorage.setItem(this.activeSessionKey, newSessionId);

    this.channel.postMessage({
      type: 'NEW_SESSION',
      sessionId: newSessionId
    });

    // Only show the notice if another LIVE tab answered
    if (this.gotPong) {
      this.newSessionNoticeSubject.next(true);

      setTimeout(() => {
        this.newSessionNoticeSubject.next(false);
      }, 6000);
    }
  }

  logout(): void {
    const mySessionId = this.getCurrentSessionId();
    const activeSessionId = localStorage.getItem(this.activeSessionKey);

    sessionStorage.removeItem(this.tabSessionKey);

    if (mySessionId && activeSessionId === mySessionId) {
      localStorage.removeItem(this.activeSessionKey);
    }

    this.invalidatedSubject.next(false);
    this.newSessionNoticeSubject.next(false);
  }

  dismissNewSessionNotice(): void {
    this.newSessionNoticeSubject.next(false);
  }

  getCurrentSessionId(): string | null {
    return sessionStorage.getItem(this.tabSessionKey);
  }

  isInvalidated(): boolean {
    return this.invalidatedSubject.value;
  }

  async ngOnDestroy(): Promise<void> {
    await this.channel.close();
  }
}